import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import api from '../api';
import Modal from '../components/Modal';
import LoadingSpinner from '../components/LoadingSpinner';
import Meta from '../components/Meta';
import './Contact.css';
import foto1 from '../assets/foto1.webp';

function Contact() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', message: '' });
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await api.post('/contact', formData);
      setModalContent({
        title: t('contact_page.modal_success_title'),
        message: t('contact_page.modal_success_message')
      });
      setIsModalOpen(true);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      setModalContent({
        title: t('contact_page.modal_error_title'),
        message: t('contact_page.modal_error_message')
      });
      setIsModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div className="contact-page">
      <Meta title={t('contact_page.meta_title')} description={t('contact_page.meta_description')} />
      {isLoading && <LoadingSpinner />}
      <section className="modern-hero" >
        <div className="hero-content">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {t('contact_page.hero_title')}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {t('contact_page.hero_subtitle')}
          </motion.p>
        </div>
      </section>

      <section className="section-container">
        <motion.div
          className="contact-info"
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2>{t('contact_page.contact_info_title')}</h2>
          <p><strong>{t('contact_page.contact_name')}</strong></p>
          <p>{t('contact_page.contact_email_label')} <a href={`mailto:${process.env.REACT_APP_CONTACT_EMAIL || 'info@vorpoint.de'}`}>{process.env.REACT_APP_CONTACT_EMAIL || 'info@vorpoint.de'}</a></p>
          <p>{t('contact_page.contact_phone_label')} {t('footer.contact_phone')}</p>
          <img src={foto1} alt="Vorpoint Büro" className="contact-info-image" />
        </motion.div>

        <motion.div
          className="contact-form-container"
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="form-title">{t('contact_page.form_title')}</h2>
          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">{t('contact_page.form_name_label')}</label>
              <motion.input
                whileFocus={{ scale: 1.02, borderColor: "var(--color-primary-dark)" }}
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">{t('contact_page.form_email_label')}</label>
              <motion.input
                whileFocus={{ scale: 1.02, borderColor: "var(--color-primary-dark)" }}
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="subject">{t('contact_page.form_subject_label')}</label>
              <motion.input
                whileFocus={{ scale: 1.02, borderColor: "var(--color-primary-dark)" }}
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="message">{t('contact_page.form_message_label')}</label>
              <motion.textarea
                whileFocus={{ scale: 1.02, borderColor: "var(--color-primary-dark)" }}
                id="message"
                name="message"
                rows="6"
                value={formData.message}
                onChange={handleChange}
                required
              ></motion.textarea>
            </div>
            <motion.button
              type="submit"
              className="submit-button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {t('contact_page.form_submit_button')}
            </motion.button>
            <p className="form-submission-info">
              {t('contact_page.form_submission_info')}
            </p>
          </form>
        </motion.div>
      </section>

      <Modal isOpen={isModalOpen} onClose={closeModal} title={modalContent.title}>
        <p>{modalContent.message}</p>
      </Modal>
    </div>
  );
}

export default Contact;